import { AuthSysBaseResponse, Response } from '@Src/_basic/object/ApiType';
import {
  CreateThirdPartyAccountRequest,
  GetThirdPartyAccountRequest,
  RegenerateTokenRequest,
  RegenerateTokenResponse,
  SearchThirdPartyAccountRequest,
  SearchThirdPartyAccountResponse,
  ThirdPartyAccountApiColumnRequest,
  ThirdPartyAccountData,
  ThirdPartyAccountFactoryRequest,
  ThirdPartyAccountSearchLimit,
  ThirdPartyAccountSearchLimitResponse,
  ThirdPartyApiColumns,
  UpdateThirdPartyAccountRequest,
} from '@Src/_basic/object/ThirdPartyAccountType';
import { Api } from '../../Api';
import { ThirdPartyAccountProtocol } from './ThirdPartyAccountProtocol';

export class ThirdPartyAccountApi {
  // 搜尋第三方廠商帳號
  public static searchAccounts(
    request: SearchThirdPartyAccountRequest,
    onSuccess?: (data: SearchThirdPartyAccountResponse) => void,
    onFailed?: (res: AuthSysBaseResponse<null>) => void,
    onFinally?: () => void,
  ) {
    Api.post(ThirdPartyAccountProtocol.SEARCH_ACCOUNT, request, undefined, onSuccess, onFailed, onFinally);
  }

  // 以ID取得第三方廠商帳號
  public static getAccountById(
    request: GetThirdPartyAccountRequest,
    onSuccess?: (data: ThirdPartyAccountData) => void,
    onFailed?: (res: AuthSysBaseResponse<null>) => void,
    onFinally?: () => void,
  ) {
    Api.post(ThirdPartyAccountProtocol.GET_ACCOUNT, request, undefined, onSuccess, onFailed, onFinally);
  }

  // 建立第三方廠商帳號
  public static createAccount(
    request: CreateThirdPartyAccountRequest,
    onSuccess?: (data: ThirdPartyAccountData) => void,
    onFailed?: (res: AuthSysBaseResponse<null>) => void,
    onFinally?: () => void,
  ) {
    Api.post(ThirdPartyAccountProtocol.CREATE_ACCOUNT, request, undefined, onSuccess, onFailed, onFinally);
  }

  // 編輯第三方廠商帳號
  public static updateAccount(
    request: UpdateThirdPartyAccountRequest,
    onSuccess?: (data: null) => void,
    onFailed?: (res: AuthSysBaseResponse<null>) => void,
    onFinally?: () => void,
  ) {
    Api.post(ThirdPartyAccountProtocol.UPDATE_ACCOUNT, request, undefined, onSuccess, onFailed, onFinally);
  }

  // 重新生成第三方廠商帳號的金鑰
  // 注意：與 AuthApi.getJWT 是同一支API，但此處只需帶 account，
  // 不可共用 AuthApi.getJWT，否則會影響登入後取得JWT的流程
  public static regenerateToken(
    request: RegenerateTokenRequest,
    onSuccess?: (data: RegenerateTokenResponse) => void,
    onFailed?: (res: AuthSysBaseResponse<null>) => void,
    onFinally?: () => void,
  ) {
    Api.post(ThirdPartyAccountProtocol.REGENERATE_TOKEN, request, undefined, onSuccess, onFailed, onFinally);
  }

  // 取得第三方廠商帳號關聯的工廠ID清單
  public static getAccountFactoryIds(
    account: string,
    onSuccess?: (data: Array<number>) => void,
    onFailed?: (res: Response<null>) => void,
    onFinally?: () => void,
  ) {
    const config = {
      params: {
        account,
      },
    };
    Api.get(ThirdPartyAccountProtocol.ACCOUNT_FACTORY, config, onSuccess, onFailed, onFinally);
  }

  // 新增帳號與工廠的關聯, 回傳新增後該帳號關聯的全部工廠ID
  public static addAccountFactories(
    request: ThirdPartyAccountFactoryRequest,
    onSuccess?: (data: Array<number>) => void,
    onFailed?: (res: Response<null>) => void,
    onFinally?: () => void,
  ) {
    Api.post(ThirdPartyAccountProtocol.ACCOUNT_FACTORY, request, undefined, onSuccess, onFailed, onFinally);
  }

  // 將帳號關聯的工廠一次改成輸入的清單 (空清單即刪除全部), 回傳修改後該帳號關聯的全部工廠ID
  public static updateAccountFactories(
    request: ThirdPartyAccountFactoryRequest,
    onSuccess?: (data: Array<number>) => void,
    onFailed?: (res: Response<null>) => void,
    onFinally?: () => void,
  ) {
    Api.put(ThirdPartyAccountProtocol.ACCOUNT_FACTORY, request, undefined, onSuccess, onFailed, onFinally);
  }

  // 刪除帳號與工廠的關聯, 回傳刪除後該帳號仍關聯的全部工廠ID
  public static deleteAccountFactories(
    request: ThirdPartyAccountFactoryRequest,
    onSuccess?: (data: Array<number>) => void,
    onFailed?: (res: Response<null>) => void,
    onFinally?: () => void,
  ) {
    const config = {
      data: request,
    };
    Api.delete(ThirdPartyAccountProtocol.ACCOUNT_FACTORY, config, onSuccess, onFailed, onFinally);
  }

  // 取得第三方廠商帳號的限制搜尋資料日期, 回傳陣列 (有該帳號設定時只有一筆, 尚未設定時為空陣列)
  public static getAccountSearchLimit(
    account: string,
    onSuccess?: (data: Array<ThirdPartyAccountSearchLimitResponse>) => void,
    onFailed?: (res: Response<null>) => void,
    onFinally?: () => void,
  ) {
    const config = {
      params: {
        account,
      },
    };
    Api.get(ThirdPartyAccountProtocol.ACCOUNT_SEARCH_LIMIT, config, onSuccess, onFailed, onFinally);
  }

  // 新增第三方廠商帳號的限制搜尋資料日期
  public static createAccountSearchLimit(
    request: ThirdPartyAccountSearchLimit,
    onSuccess?: (data: ThirdPartyAccountSearchLimitResponse) => void,
    onFailed?: (res: Response<null>) => void,
    onFinally?: () => void,
  ) {
    Api.post(ThirdPartyAccountProtocol.ACCOUNT_SEARCH_LIMIT, request, undefined, onSuccess, onFailed, onFinally);
  }

  // 修改第三方廠商帳號的限制搜尋資料日期
  public static updateAccountSearchLimit(
    request: ThirdPartyAccountSearchLimit,
    onSuccess?: (data: ThirdPartyAccountSearchLimitResponse) => void,
    onFailed?: (res: Response<null>) => void,
    onFinally?: () => void,
  ) {
    Api.put(ThirdPartyAccountProtocol.ACCOUNT_SEARCH_LIMIT, request, undefined, onSuccess, onFailed, onFinally);
  }

  // 取得 /thirdparty 底下每支API回傳資料的全部欄位名稱
  public static getApiColumns(
    onSuccess?: (data: Array<ThirdPartyApiColumns>) => void,
    onFailed?: (res: Response<null>) => void,
    onFinally?: () => void,
  ) {
    Api.get(ThirdPartyAccountProtocol.API_COLUMNS, undefined, onSuccess, onFailed, onFinally);
  }

  // 取得帳號有設定關聯的API回傳資料欄位, 依API路徑分組 (未設定任何欄位的API不會出現在回傳陣列中)
  public static getAccountApiColumns(
    account: string,
    onSuccess?: (data: Array<ThirdPartyAccountApiColumnRequest>) => void,
    onFailed?: (res: Response<null>) => void,
    onFinally?: () => void,
  ) {
    const config = {
      params: {
        account,
      },
    };
    Api.get(ThirdPartyAccountProtocol.ACCOUNT_API_COLUMN, config, onSuccess, onFailed, onFinally);
  }

  // 新增帳號與某支API多個回傳資料欄位的關聯, 回傳新增後該帳號在該API關聯的全部欄位
  public static addAccountApiColumns(
    request: ThirdPartyAccountApiColumnRequest,
    onSuccess?: (data: ThirdPartyAccountApiColumnRequest) => void,
    onFailed?: (res: Response<null>) => void,
    onFinally?: () => void,
  ) {
    Api.post(ThirdPartyAccountProtocol.ACCOUNT_API_COLUMN, request, undefined, onSuccess, onFailed, onFinally);
  }

  // 一次修改多支API的關聯欄位, 每筆將帳號在該API關聯的回傳資料欄位改成輸入的清單 (空清單即刪除該API全部關聯),
  // 全部設定都正確才會修改, 陣列不可為空, 回傳修改後每支API關聯的全部欄位
  public static updateAccountApiColumns(
    request: Array<ThirdPartyAccountApiColumnRequest>,
    onSuccess?: (data: Array<ThirdPartyAccountApiColumnRequest>) => void,
    onFailed?: (res: Response<null>) => void,
    onFinally?: () => void,
  ) {
    Api.put(ThirdPartyAccountProtocol.ACCOUNT_API_COLUMN, request, undefined, onSuccess, onFailed, onFinally);
  }

  // 刪除帳號與某支API多個回傳資料欄位的關聯, 回傳刪除後該帳號在該API仍關聯的全部欄位
  public static deleteAccountApiColumns(
    request: ThirdPartyAccountApiColumnRequest,
    onSuccess?: (data: ThirdPartyAccountApiColumnRequest) => void,
    onFailed?: (res: Response<null>) => void,
    onFinally?: () => void,
  ) {
    const config = {
      data: request,
    };
    Api.delete(ThirdPartyAccountProtocol.ACCOUNT_API_COLUMN, config, onSuccess, onFailed, onFinally);
  }
}
